const hasExplicitTimeZone = (value: string) =>
  /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);

const parseReceivedAtUtc = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = hasExplicitTimeZone(trimmed)
    ? trimmed
    : `${trimmed.replace(' ', 'T')}Z`;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
};

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const formatInZone = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
  timeZone = getBrowserTimeZone(),
) => new Intl.DateTimeFormat('en-US', { ...options, timeZone }).format(date);

export const formatReceivedAt = (value: string, timeZone?: string) => {
  const date = parseReceivedAtUtc(value);
  if (!date) return { date: '', time: '' };

  return {
    date: formatInZone(
      date,
      { month: '2-digit', day: '2-digit', year: 'numeric' },
      timeZone,
    ),
    time: formatInZone(
      date,
      { hour: 'numeric', minute: '2-digit', hour12: true },
      timeZone,
    ),
  };
};

export const formatReceivedAtDateTime = (value: string, timeZone?: string) => {
  const date = parseReceivedAtUtc(value);
  if (!date) return '';

  return formatInZone(
    date,
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
    timeZone,
  );
};

export const getLocalDayUtcBounds = (date: Date) => {
  if (getBrowserTimeZone() === 'UTC') {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    return {
      receivedAtStart: new Date(Date.UTC(year, month, day)).toISOString(),
      receivedAtEnd: new Date(Date.UTC(year, month, day + 1)).toISOString(),
    };
  }

  return {
    receivedAtStart: new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    ).toISOString(),
    receivedAtEnd: new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() + 1,
    ).toISOString(),
  };
};

export const getReceivedAtTooltipCopy = () =>
  'Times on this page are converted to your local timezone, with a fallback to UTC if your browser time is undetectable. The rest of Predoc remains unconverted (generally displayed in UTC).';
